import { useEffect, useState, FormEvent } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import LockIcon from '@mui/icons-material/Lock';
import PushPinIcon from '@mui/icons-material/PushPin';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import { carUrl } from '../lib/slugId';
import { useAuth } from '../auth/AuthContext';
import { useDateTimeFormat } from '../preferences/DateTimeFormatContext';
import { PageHeader } from '../components/PageHeader';
import { MarkdownView } from '../components/MarkdownView';
import { MarkdownEditor } from '../components/MarkdownEditor';
import type { ForumPost, ForumThread, SingleResponse } from '../api/types';

export function ForumThreadDetail() {
  const { slugId } = useParams<{ slugId: string }>();
  const { t } = useTranslation();
  const { user } = useAuth();
  const { formatDateTime } = useDateTimeFormat();
  const [thread, setThread] = useState<ForumThread | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  useEffect(() => {
    if (!slugId) return;
    apiFetch<SingleResponse<ForumThread>>(`/api/v1/forum/threads/${slugId}`)
      .then((res) => setThread(res.data))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [slugId]);

  const handleReply = async (e: FormEvent) => {
    e.preventDefault();
    if (!thread || !body.trim()) return;
    setSending(true);
    try {
      const res = await apiFetch<SingleResponse<ForumPost>>(
        `/api/v1/forum/threads/${thread.id}/posts`,
        {
          method: 'POST',
          body: JSON.stringify({ body: body.trim() }),
        }
      );
      setThread((prev) =>
        prev
          ? {
              ...prev,
              posts: [...(prev.posts ?? []), res.data],
              last_post_at: res.data.created_at,
            }
          : prev
      );
      setBody('');
    } finally {
      setSending(false);
    }
  };

  const handleDeletePost = async (post: ForumPost) => {
    if (!window.confirm(t('forumThread.deletePostConfirm'))) return;
    setDeletingId(post.id);
    try {
      await apiFetch(`/api/v1/forum/posts/${post.id}`, { method: 'DELETE' });
      setThread((prev) =>
        prev
          ? { ...prev, posts: (prev.posts ?? []).filter((p) => p.id !== post.id) }
          : prev
      );
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !thread) {
    return (
      <Container maxWidth="md" sx={{ py: { xs: 3, md: 4 } }}>
        <Alert severity="error">{error || t('forumThread.notFound')}</Alert>
      </Container>
    );
  }

  const posts = thread.posts ?? [];

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 4 } }}>
      <PageHeader
        crumbs={[
          { label: t('nav.forum'), to: '/forum' },
          { label: thread.title },
        ]}
      />
      <Stack spacing={3}>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
            {thread.is_pinned && (
              <Chip size="small" icon={<PushPinIcon />} label={t('forum.pinned')} color="primary" />
            )}
            {thread.is_locked && (
              <Chip size="small" icon={<LockIcon />} label={t('forumThread.locked')} />
            )}
            {thread.category && (
              <Chip size="small" label={thread.category.name} variant="outlined" />
            )}
            {thread.car && (
              <Chip
                size="small"
                icon={<DirectionsCarIcon />}
                label={thread.car.display_name}
                component={RouterLink}
                to={carUrl(thread.car)}
                clickable
                color="primary"
                variant="outlined"
              />
            )}
          </Stack>
          <Typography variant="h4">{thread.title}</Typography>
        </Stack>

        <Paper variant="outlined" sx={{ p: 2.5 }}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Avatar src={thread.user?.avatar_url ?? undefined}>
              {thread.user?.name?.[0] ?? '?'}
            </Avatar>
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="baseline"
                spacing={1}
              >
                <Typography variant="subtitle2" fontWeight={600}>
                  {thread.user?.name ?? '—'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatDateTime(thread.created_at)}
                </Typography>
              </Stack>
              <Box sx={{ mt: 1 }}>
                <MarkdownView>{thread.body}</MarkdownView>
              </Box>
            </Box>
          </Stack>
        </Paper>

        <Divider>
          <Typography variant="overline" color="text.secondary">
            {t('forumThread.repliesHeading', { count: posts.length })}
          </Typography>
        </Divider>

        {posts.length === 0 ? (
          <Typography color="text.secondary">{t('forumThread.noReplies')}</Typography>
        ) : (
          <Stack spacing={1.5}>
            {posts.map((post) => {
              const isMine = post.user_id === user?.id;
              return (
                <Paper key={post.id} variant="outlined" sx={{ p: 2 }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Avatar src={post.user?.avatar_url ?? undefined}>
                      {post.user?.name?.[0] ?? '?'}
                    </Avatar>
                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="baseline"
                        spacing={1}
                      >
                        <Typography variant="subtitle2" fontWeight={600}>
                          {post.user?.name ?? '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(post.created_at)}
                        </Typography>
                      </Stack>
                      <Box sx={{ mt: 1 }}>
                        <MarkdownView>{post.body}</MarkdownView>
                      </Box>
                    </Box>
                    {isMine && (
                      <IconButton
                        size="small"
                        onClick={() => handleDeletePost(post)}
                        disabled={deletingId === post.id}
                        aria-label={t('common.delete')}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
        )}

        {thread.is_locked ? (
          <Alert severity="info">{t('forumThread.lockedNotice')}</Alert>
        ) : (
          <Box component="form" onSubmit={handleReply}>
            <Stack spacing={1.5}>
              <MarkdownEditor
                label={t('forumThread.replyHeading')}
                value={body}
                onChange={setBody}
                placeholder={t('forumThread.replyPlaceholder')}
                rows={6}
              />
              <Stack direction="row" justifyContent="flex-end">
                <Button
                  type="submit"
                  variant="contained"
                  endIcon={<SendIcon />}
                  disabled={sending || !body.trim()}
                >
                  {sending ? t('forumThread.sending') : t('forumThread.send')}
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}
      </Stack>
    </Container>
  );
}
